<script setup lang="ts">
import { useSlots } from "vue"
import {
  ContextMenuItem,
  ContextMenuPortal,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger
} from "reka-ui"
import LoadingSpinner from "@/components/loading/LoadingSpinner.vue"
import GeneralButton from "@/components/inputs/GeneralButton.vue"

const props = withDefaults(
  defineProps<{
    disabled?: boolean
    loading?: boolean
  }>(),
  {
    disabled: false,
    loading: false
  }
)

defineOptions({
  inheritAttrs: false
})

const slots = useSlots()

function preventWhenLoading(event: Event) {
  if (props.loading) {
    event.preventDefault()
    event.stopPropagation()
  }
}
</script>

<template>
  <ContextMenuSub v-if="slots.submenu">
    <ContextMenuSubTrigger
      v-bind="$attrs"
      :disabled="disabled || loading"
      class="data-highlighted:bg-gray-lighten-2 relative flex cursor-pointer items-center justify-between px-2 py-1.5 text-sm outline-none select-none data-disabled:cursor-not-allowed! data-disabled:opacity-30"
      @click.capture="preventWhenLoading"
      @contextmenu.capture="preventWhenLoading"
    >
      <div class="w-full space-x-2">
        <slot />
      </div>
      <GeneralButton
        v-if="!loading && !disabled"
        variant="text"
        title="Submenu weergeven"
        size="small"
        :icon="['fas', 'chevron-right']"
      />
      <LoadingSpinner v-else-if="loading" class="size-4" />
    </ContextMenuSubTrigger>

    <ContextMenuPortal>
      <ContextMenuSubContent
        class="divide-gray-lighten-2 surface border-gray-lighten-1 z-50 min-w-64 divide-y rounded-md border focus:outline-hidden"
        :collision-padding="4"
        :side-offset="2"
        loop
        @pointerdown.stop
        @touchstart.stop
        @click.stop
        @contextmenu.prevent.stop
      >
        <slot name="submenu" />
      </ContextMenuSubContent>
    </ContextMenuPortal>
  </ContextMenuSub>

  <ContextMenuItem
    v-else
    v-bind="$attrs"
    :disabled="disabled || loading"
    class="data-highlighted:bg-gray-lighten-2 relative flex cursor-pointer items-center justify-between px-2 py-1.5 text-sm outline-none select-none data-disabled:cursor-not-allowed! data-disabled:opacity-30"
    :class="loading ? 'cursor-wait!' : ''"
    @click.capture="preventWhenLoading"
    @contextmenu.capture="preventWhenLoading"
  >
    <div class="w-full space-x-2">
      <slot />
    </div>
    <LoadingSpinner v-if="loading" class="size-4" />
  </ContextMenuItem>
</template>
